// src/atoms/network.js
import moment from "moment-timezone";
import { CONFIG } from "./config";

let currentBusinessID = null;
let currentStripePK = null; // ⬅️ holds the business publishable key

// ───────────────────────── helpers ─────────────────────────
function truthy(v) {
  return v === true || v === "true" || v === 1 || v === "1";
}

function toNumber(n, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

function formatPhoneNumber(raw) {
  if (!raw) return "";
  if (raw.startsWith("+")) return raw;
  let num = raw.replace(/\D+/g, "");
  if (num.startsWith("0")) num = "+61" + num.slice(1);
  return num.startsWith("+") ? num : "+" + num;
}

function toBoundary() {
  return "Boundary-" + Math.random().toString(16).slice(2);
}

async function postB64(body) {
  const boundary = toBoundary();
  const base64Body =
    typeof window !== "undefined" && typeof window.btoa === "function"
      ? window.btoa(JSON.stringify(body))
      : Buffer.from(JSON.stringify(body), "utf8").toString("base64");

  const multipart =
    `--${boundary}\r\n` +
    `Content-Disposition: form-data; name="b64"\r\n\r\n` +
    `${base64Body}\r\n` +
    `--${boundary}--\r\n`;

  const resp = await fetch(CONFIG.apiUrl, {
    method: "POST",
    headers: { "Content-Type": `multipart/form-data; boundary=${boundary}` },
    body: multipart,
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`HTTP ${resp.status}${text ? ` – ${text}` : ""}`);
  }
  return resp.json();
}

/** Try to find a publishable key in various possible fields. */
function extractStripePublishableKey(data) {
  if (!data || typeof data !== "object") return null;
  const candidates = [
    data.stripe_pk,
    data.stripePublishableKey,
    data.stripe_publishable_key,
    data.publishable_key,
    data.publishableKey,
    data.primary_key,
    data.primaryKey,
    data.pk,
    data.key,
  ];
  const pk = candidates.find(
    (v) => typeof v === "string" && /^pk_(test|live)_/.test(v)
  );
  return pk || null;
}

async function networkRequest(
  cmd,
  itemID,
  name = "",
  phone = "",
  twoFa = "",
  businessID,
  tz = moment.tz.guess()
) {
  const bodyDict = {
    item_id: itemID,
    name,
    phone: formatPhoneNumber(phone),
    business_id: businessID || currentBusinessID,
    tz,
    two_fa: (twoFa || "").trim(),
    cmd,
  };

  const json = await postB64(bodyDict);

  // Normalize common flags if present
  if (json?.data) {
    const d = json.data;
    if ("auth" in d) d.auth = truthy(d.auth);
    if ("unlocked" in d && !("auth" in d)) d.auth = truthy(d.unlocked);
  }

  return json;
}

// ───────────────────────── getters ─────────────────────────
export function getCurrentBusinessID() {
  return currentBusinessID;
}
export function getCurrentStripePK() {
  return currentStripePK;
}

// ───────────────────────── basic API ───────────────────────
export async function getSite(itemID, twoFa = "") {
  const result = await networkRequest(
    "get-site-app-clip-instant-app-item",
    itemID,
    "",
    "",
    twoFa
  );

  // Keep business ID in sync
  if (result?.data?.business_id) {
    currentBusinessID = Number(result.data.business_id);
  }

  // Capture Stripe publishable key for this business
  
  const pk = result?.data.stripe_key;
  console.log(pk, 'qweqweqwemqwhe,mwqhejqwhejkqwhekjqwhj')
  if (pk) currentStripePK = pk;
  // Normalize item numeric/bool fields
  if (result?.data?.item) {
    const item = result.data.item;
    if ("is_ajax_item" in item) item.is_ajax_item = truthy(item.is_ajax_item);
    if ("hire_length" in item) item.hire_length = toNumber(item.hire_length);
    if ("hire_max_length" in item)
      item.hire_max_length = toNumber(item.hire_max_length);
    if ("hire_cost_base" in item)
      item.hire_cost_base = Number(item.hire_cost_base);
  }

  return result;
}

export async function checkUserAuth(itemID, name, phone, twoFa = "", businessID) {
  return networkRequest(
    "authenticate-check-item",
    itemID,
    name,
    phone,
    twoFa,
    businessID
  );
}

export async function confirmVerificationCode(itemID, name, phone, twoFa, businessID) {
  return networkRequest(
    "compare-2FA-item",
    itemID,
    name,
    phone,
    twoFa,
    businessID
  );
}

export async function unlockDoor(itemID, name, phone, twoFa, businessID) {
  return networkRequest(
    "unlock-after-2FA-item",
    itemID,
    name,
    phone,
    twoFa,
    businessID
  );
}

export async function unlockAgain(itemID, name, phone, twoFa = "", businessID) {
  return networkRequest(
    "authenticate-unlock-door-item",
    itemID,
    name,
    phone,
    twoFa,
    businessID
  );
}

// ─────────────── payments & sessions (initial hire) ───────────────
export async function fetchHireableItemPaymentIntent({
  itemID,
  phone,
  minutes,
  totalCents,
  businessID,
}) {
  const body = {
    cmd: "fetch-hireable-item-payment-intent",
    item_id: itemID,
    phone: formatPhoneNumber(phone),
    business_id: businessID || currentBusinessID,
    minutes,
    total: totalCents, // cents
  };

  const json = await postB64(body);
  const data = json?.data || {};
  const secret = data?.intent?.client_secret;
  const txId = data?.transaction_id;

  if (!secret || !txId) {
    throw new Error("Missing client_secret or transaction_id");
  }
  return { clientSecret: secret, transactionId: txId };
}

export async function createHiredSession({
  transactionId,
  minutes,
  itemID,
  phone,
  businessID,
}) {
  const body = {
    cmd: "create-hired-session",
    transaction_id: transactionId,
    minutes,
    item_id: itemID,
    phone: formatPhoneNumber(phone),
    business_id: businessID || currentBusinessID,
  };

  const json = await postB64(body);
  const d = json?.data || {};
  const sessionId = d?.session_id;
  const length = d?.length;

  if (!sessionId || !length) {
    throw new Error("Failed to create session");
  }
  return { sessionId, length };
}

export async function activateHiredSession({
  sessionId,
  minutes,
  itemID,
  phone,
  businessID,
}) {
  const body = {
    cmd: "hireable-item-activate",
    session_id: sessionId,
    hire_length: minutes,
    item_id: itemID,
    phone: formatPhoneNumber(phone),
    business_id: businessID || currentBusinessID,
  };
  const json = await postB64(body);
  return json;
}

export async function deactivateHiredSession({
  sessionId,
  itemID
}) {
  const body = {
    cmd: "hireable-item-deactivate",
    session_id: sessionId,
    item_id: itemID
  };
  const json = await postB64(body);
  return json;
}

// ─────────────── payments & sessions (extension) ───────────────
export async function fetchHireableItemExtendPaymentIntent({
  itemID,
  sessionId,
  minutes,
  totalCents,
  businessID,
}) {
  const body = {
    cmd: "fetch-hireable-item-extend-payment-intent",
    item_id: itemID,
    session_id: sessionId,
    business_id: businessID || currentBusinessID,
    minutes,
    total: totalCents,
  };

  const json = await postB64(body);
  const data = json?.data || {};
  const secret = data?.intent?.client_secret;
  const txId = data?.transaction_id;

  if (!secret || !txId) {
    throw new Error("Missing client_secret or transaction_id (extend)");
  }
  return { clientSecret: secret, transactionId: txId };
}

export async function extendHiredSession({
  sessionId,
  minutes,
  transactionId,
  businessID,
}) {
  const body = {
    cmd: "extend-hired-session",
    session_id: sessionId,
    minutes,
    transaction_id: transactionId,
    business_id: businessID || currentBusinessID,
  };

  const json = await postB64(body);
  const d = json?.data || {};
  const newTotalMinutes = toNumber(d?.length, NaN);
  const remainingSeconds = toNumber(d?.remain_seconds, NaN);

  if (!Number.isFinite(newTotalMinutes) || !Number.isFinite(remainingSeconds)) {
    throw new Error("Failed to extend session");
  }
  return { newTotalMinutes, remainingSeconds };
}

// ADD near the bottom of src/atoms/network.js

export async function reportDamage({
  siteID,
  itemID,
  note,
  phone,
  photo1Base64,
  photo2Base64,
  photo3Base64,
}) {
  const body = {
    cmd: "report-damage",
    site_id: Number(siteID),
    item_id: Number(itemID),
    note: note || "",
    phone,
    business_id: currentBusinessID,
    tz: moment.tz.guess(),
  };

  if (photo1Base64) body.photo1_base64 = photo1Base64;
  if (photo2Base64) body.photo2_base64 = photo2Base64;
  if (photo3Base64) body.photo3_base64 = photo3Base64;

  return postB64(body);
}
